import i18next from 'i18next'

// The services behind this app answer a failed call in more than one shape:
//
//   mekome api      { errorDescription: '...' }
//                   { errors: [{ errorDescription: '...' }] }
//   protocol api    { type, title: 'One or more validation errors occurred.',
//                     status: 400, errors: { Description: ['The Description field is required.'] },
//                     traceId: '...' }                       (RFC 9110 problem details)
//
// Only the first shape used to be read, so a rejected save surfaced axios'
// 'Request failed with status code 400' - or an empty snackbar - instead of the
// reason the server gave. Everything here turns any of those shapes into lines
// a user can read.

// Problem details name the server-side field ('Description'), which says
// nothing to a Hebrew-speaking user; the fields the web forms can actually get
// wrong are given their form label instead.
const FIELD_LABEL_KEYS: Record<string, string> = {
  description: 'protocols:description',
  categoryname: 'protocols:category_name',
  protocoldate: 'protocols:protocol_date',
}

const isRequiredMessage = (message: string) => /is required/i.test(message)

// Some labels carry their own punctuation ('תאריך פרוטוקול:'), which reads
// wrong once it sits inside a sentence.
const labelFor = (field: string): string => {
  const key = FIELD_LABEL_KEYS[field.trim().toLowerCase()]
  return key ? i18next.t(key).replace(/\s*:\s*$/, '') : ''
}

const text = (value: any): string => (typeof value === 'string' ? value.trim() : '')

const toMessageList = (value: any): string[] =>
  (Array.isArray(value) ? value : [value]).map(text).filter(Boolean)

const readValidationMessages = (errors: Record<string, any>): string[] =>
  Object.keys(errors).reduce<string[]>((acc, field) => {
    const label = labelFor(field)
    toMessageList(errors[field]).forEach(message => {
      acc.push(
        label && isRequiredMessage(message)
          ? i18next.t('app:field_required', { field: label })
          : message
      )
    })
    return acc
  }, [])

/**
 * Every readable message a failed response carries, most specific first.
 * Empty when the payload holds nothing worth showing.
 */
export const readApiErrorMessages = (data: any): string[] => {
  if (!data) return []
  if (typeof data === 'string') return toMessageList(data)

  const messages: string[] = []
  const pushed = (message: string) => messages.indexOf(message) !== -1

  const add = (message: string) => {
    if (message && !pushed(message)) messages.push(message)
  }

  add(text(data.errorDescription))

  const { errors } = data
  if (Array.isArray(errors)) {
    errors.forEach(entry => add(text(entry) || text(entry?.errorDescription) || text(entry?.message)))
  } else if (errors && typeof errors === 'object') {
    readValidationMessages(errors).forEach(add)
  }

  // Problem details put the readable part on `detail`; `title` is the generic
  // 'One or more validation errors occurred.', so it is only worth showing when
  // nothing more specific came along.
  add(text(data.detail))
  add(text(data.message))
  if (!messages.length) add(text(data.title))

  return messages
}

/**
 * The fields a problem-details response rejected, lowercased ('description',
 * 'categoryname'), so a form can mark the inputs it has to fix. Empty for the
 * shapes that name no field.
 */
export const readApiErrorFields = (data: any): string[] => {
  const errors = data?.errors
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return []
  return Object.keys(errors).map(field => field.trim().toLowerCase()).filter(Boolean)
}

/**
 * One line to show the user for a failed response, falling back to the generic
 * server message so a failed save is never silent.
 */
export const readApiErrorMessage = (data: any, fallback?: string): string => {
  const messages = readApiErrorMessages(data)
  if (messages.length) return messages.join(' ')
  return text(fallback) || i18next.t('app:server_problem')
}
